package ru.villawonka.app.presentation.sharedviews

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.PageSize
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.FilterQuality
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import kotlinx.coroutines.Dispatchers
import ru.villawonka.app.presentation.design.components.AppIconButton
import ru.villawonka.app.presentation.design.components.AppIconButtonSize
import ru.villawonka.app.presentation.design.components.AppIconButtonType
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_expand_24

enum class PhotoCarouselType {
    COMPACT,
    EXPANDED
}

@Composable
internal fun PhotoCarouselView(
    type: PhotoCarouselType,
    photos: List<String>,
    maxWidth: Float,
    badge: @Composable ((Int) -> Unit)? = null,
    onImageEnlargeClick: (selectedIndex: Int) -> Unit,
    userScrollEnabled: Boolean = true,
) {
    val containerBetweenSpacing = 8.dp
    val localDensity = LocalDensity.current

    val containerWidthMultiplier = remember(type) {
        when (type) {
            PhotoCarouselType.COMPACT -> 0.89f
            PhotoCarouselType.EXPANDED -> 0.89f
        }
    }
    val containerHeightMultiplier = remember(type) {
        when (type) {
            PhotoCarouselType.COMPACT -> 0.55f
            PhotoCarouselType.EXPANDED -> 0.70f
        }
    }
    val containerWidth = remember { (maxWidth * containerWidthMultiplier).dp }
    val containerHeight = remember { (containerWidth.value * containerHeightMultiplier).dp }



    HorizontalPager(
        key = { photos[it] },
        state = rememberPagerState(pageCount = { photos.size }),
        pageSize = PageSize.Fixed(containerWidth),
        contentPadding = PaddingValues(horizontal = containerBetweenSpacing),
        userScrollEnabled = userScrollEnabled,
    ) { page ->
        Box(
            modifier = Modifier
                .padding(end = if (page < photos.size - 1) containerBetweenSpacing else 0.dp)
                .clip(RoundedCornerShape(12.dp))
        ) {
            ImageContent(
                type = type,
                url = photos[page],
                width = containerWidth,
                height = containerHeight,
                density = localDensity,
                onImageEnlargeClick = { onImageEnlargeClick(page) }
            )

            badge?.invoke(page)

            if (type == PhotoCarouselType.COMPACT) AppIconButton(
                type = AppIconButtonType.SEMI_TRANSPARENT,
                size = AppIconButtonSize.SMALL,
                iconRes = Res.drawable.ic_expand_24,
                onClick = { onImageEnlargeClick(page) },
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(5.dp)
            )
        }
    }
}

@Composable
private fun ImageContent(
    type: PhotoCarouselType,
    onImageEnlargeClick: () -> Unit,
    url: String,
    width: Dp,
    height: Dp,
    density: Density
) {
    val widthInPx = remember(width) { (width.value * density.density).toInt() }
    val heightInPx = remember(height) { (height.value * density.density).toInt() }

    val painterResource = asyncPainterResource(
        data = url,
        filterQuality = FilterQuality.Low,
    ) {
        this.coroutineContext = Dispatchers.Default
        this.maxBitmapDecodeSize = IntSize(widthInPx, heightInPx)
        this.density = density
    }

    KamelImage(
        resource = { painterResource },
        contentDescription = null,
        contentScale = ContentScale.Crop,
        modifier = Modifier
            .size(width, height)
            .let {
                if (type == PhotoCarouselType.EXPANDED) {
                    it.clickable(onClick = onImageEnlargeClick)
                } else {
                    it
                }
            },
        onLoading = { progress ->
            Box(Modifier.fillMaxSize()) {
                CircularProgressIndicator(
                    progress = { progress },
                    modifier = Modifier.align(Alignment.Center)
                )
            }
        }
    )
}